import { createContext, useContext, useEffect, useState } from "react";
import axios from "axios";
import localforage from "localforage";

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

const NewsContext = createContext();

export const NewsProvider = ({ children }) => {
  const [news, setNews] = useState([]);
  const [globalNews, setGlobalNews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // ✅ Load cached data or fetch new ones
  useEffect(() => {
    loadCachedNews();
  }, []);

  const loadCachedNews = async () => {
    try {
      const cachedLocal = await localforage.getItem("newsCache");
      const cachedGlobal = await localforage.getItem("globalNewsCache");

      if (cachedLocal && cachedGlobal) {
        console.log("✅ Loaded cached news data");
        setNews(cachedLocal);
        setGlobalNews(cachedGlobal);
      } else {
        console.log("No cache found, fetching news...");
        fetchNews();
      }
    } catch (err) {
      console.error("Failed to load cached news:", err);
      fetchNews();
    }
  };

  // ✅ Fetch latest news (local + global)
  const fetchNews = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log("Fetching latest news data...");

      // 1️⃣ Local news first
      const localRes = await axios.get(`${BASE_URL}/api/news`);
      const localNews = localRes.data || [];
      setNews(localNews);
      await localforage.setItem("newsCache", localNews);
      console.log("Local news cached successfully");

      // 2️⃣ Global news in background
      axios
        .get(`${BASE_URL}/api/globalnews`)
        .then(async (res) => {
          const feed = res?.data?.data?.feed;
          const globalFeed = Array.isArray(feed) ? feed : [];
          setGlobalNews(globalFeed);
          await localforage.setItem("globalNewsCache", globalFeed);
          console.log("Global news cached successfully");
        })
        .catch((err) => {
          console.error("Error fetching global news:", err.message);
        });
    } catch (err) {
      console.error("Error fetching local news:", err.message);
      setError("Failed to fetch news. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <NewsContext.Provider
      value={{
        news,
        globalNews,
        loading,
        error,
        fetchNews,
      }}
    >
      {children}
    </NewsContext.Provider>
  );
};

// ✅ Custom hook for consuming context
export const useNews = () => useContext(NewsContext);
